/** @jsxImportSource @emotion/react */
import {
  Autocomplete,
  Backdrop,
  CircularProgress,
  css,
  TextField,
} from "@mui/material";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import React, { useState, VFC } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import { AiOutlineWarning } from "react-icons/ai";
import { CountryPath } from "src/entities/CountryPath";
import {
  BusinessCategory,
  PublisherResponseV3,
} from "src/entities/v3/PublisherResponse";
import { Auth } from "src/hooks/useAuth";
import { Refetch, useFetch } from "src/hooks/useFetch";
import { useUpdatePublisherV3 } from "src/hooks/useUpdatePublisherV3";
import getCountryCode from "src/utils/getContryCode";
import Button from "../components/common/Button";
import InputField from "../components/common/InputField";
import EditPublisherInfoHelpModal from "../components/modal/EditPublisherInfoHelpModal";
import PageHeader from "../components/page/PageHeader";
import PageWrapper from "../components/page/PageWrapper";

interface Props {
  auth: Auth;
  country: CountryPath;
  refetchPublisher: Refetch<PublisherResponseV3>;
}

const EditPublisherInfoPage: VFC<Props> = (props: Props) => {
  /**
   * Common parameters
   */
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState<boolean>(false);

  /**
   * company info
   */
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [headquarters, setHeadquarters] = useState("");
  const [founded, setFounded] = useState(0);
  const [facebook, setFacebook] = useState("");
  const [youtube, setYoutube] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [instagram, setInstagram] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [twitter, setTwitter] = useState("");
  const [businessCategories, setBusinessCategories] = useState<
    BusinessCategory[]
  >([]);
  const { acceptedFiles, getRootProps, getInputProps, isDragAccept } =
    useDropzone({
      multiple: false,
      maxSize: 5 * 1024 * 1024,
      onDropRejected: () => {
        enqueueSnackbar("File size exceeds the 5MB limit", {
          variant: "error",
        });
      },
    });

  /**
   * Get publisher (v3)
   */
  const { data: publisherResponse, isLoading: isPublisherLoading } =
    useFetch<PublisherResponseV3>({
      url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/publishers`,
      onSuccess: (data) => {
        console.log(data);
        if (data) {
          setName(data.name);
          setWebUrl(data.webSiteUrl);
          setPhoneNumber(data.phoneNumber);
          setDescription(data.summary);
          setFounded(data.founded);
          setHeadquarters(data.headquarters);
          setFacebook(data.facebookUrl);
          setTwitter(data.twitterUrl);
          setLinkedin(data.linkedinUrl);
          setYoutube(data.youtubeUrl);
          setTiktok(data.tiktokUrl);
          setInstagram(data.instagramUrl);
          setBusinessCategories(data.businessCategories);
        }
      },
      onError: () => {
        enqueueSnackbar(t("error.login_again"), {
          variant: "error",
        });
      },
    });

  /**
   * Dropdown settings
   */
  /**
   * Dropdown parameters
   */
  const [backdropOpen, setBackdropOpen] = useState(false);
  /**
   * Open backdrop
   */
  const handleBackDropOpen = () => {
    setBackdropOpen(true);
  };
  /**
   * Close backdrop
   */
  const handleBackdropClose = () => {
    setBackdropOpen(false);
  };

  const { updateInfo } = useUpdatePublisherV3({
    input: {
      publisherId: publisherResponse?.id,
      acceptedFiles: acceptedFiles,
      name: name,
      description: description,
      webUrl: webUrl,
      headquarters: headquarters,
      phoneNumber: phoneNumber,
      founded: founded,
      logoUrl: publisherResponse?.logoUrl,
      twitter: twitter,
      facebook: facebook,
      tiktok: tiktok,
      youtube: youtube,
      instagram: instagram,
      linkedin: linkedin,
      businessCategories: businessCategories,
    },
    functions: {
      handleBackDropOpen: handleBackDropOpen,
      handleBackdropClose: handleBackdropClose,
      refetchPublisher: async () => {
        await props.refetchPublisher();
      },
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <EditPublisherInfoHelpModal
        open={open}
        handleClose={() => {
          setOpen(false);
        }}
        href={`https://www.vritimes.com/${getCountryCode(
          props.country
        )}/articles/${publisherResponse?.id}`}
      />
      <PageWrapper>
        <Backdrop sx={{ color: "#fff", zIndex: 10001 }} open={backdropOpen}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <PageHeader
          title={t("edit_company_info.page_header")}
          isHelp
          onClickHelp={() => {
            setOpen(true);
          }}
        />
        {isPublisherLoading ? (
          <div className="flex h-full w-full justify-center">
            <CircularProgress />
          </div>
        ) : publisherResponse ? (
          <>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div className="flex items-center mb-2">
                <div
                  css={contentTitle}
                  className="flex items-center"
                  style={{ marginBottom: 0 }}
                >
                  {t("edit_company_info.content.name")}
                </div>
                <div className="whitespace-nowrap flex ml-2 text-xs text-gray-500">
                  <AiOutlineWarning size="16px" style={{ marginRight: 4 }} />
                  {t("edit_company_info.aioutline_warning")}
                </div>
              </div>
              <InputField
                value={name}
                type="text"
                onChange={(value) => {
                  setName(value);
                }}
                placeholder={t("edit_company_info.content.name_placeholder")}
                disabled
              />
            </div>
            <div style={{ width: "75%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.description")}
              </div>
              <InputField
                value={description}
                multiline
                type="text"
                onChange={(value) => {
                  setDescription(value);
                }}
                placeholder={t(
                  "edit_company_info.content.description_placeholder"
                )}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.company_logo")}
              </div>
              <div style={{ display: "flex" }}>
                <div
                  css={thumbnailStyle}
                  style={{
                    borderColor: isDragAccept ? "#00e676" : "#eeeeee",
                  }}
                  {...getRootProps()}
                >
                  <input {...getInputProps()} />
                  <p>
                    {t("edit_company_info.content.company_logo_placeholder_1")}
                  </p>
                  <p>
                    {t("edit_company_info.content.company_logo_placeholder_2")}
                  </p>
                </div>
                <div>
                  {acceptedFiles.length > 0 ? (
                    acceptedFiles.map((file) => {
                      return (
                        <div className="border ml-4 rounded-lg">
                          <img
                            className="object-cover rounded-lg"
                            key={file.name}
                            src={URL.createObjectURL(file)}
                            alt="preview"
                            style={{ height: 120, width: 120 }}
                          />
                        </div>
                      );
                    })
                  ) : publisherResponse?.logoUrl ? (
                    <div className="border ml-4 rounded-lg">
                      <img
                        className="object-cover rounded-lg"
                        key="thumbnail"
                        src={publisherResponse?.logoUrl}
                        alt="preview"
                        style={{ height: 120, width: 120 }}
                      />
                    </div>
                  ) : (
                    <div
                      className="border rounded-lg"
                      style={{ height: 120, width: 120, marginLeft: 24 }}
                    />
                  )}
                </div>
              </div>
            </div>
            <div style={{ width: "75%", marginBottom: 24 }}>
              <div css={contentTitle}>*Business Categories (Max 3)</div>
              <Autocomplete
                multiple
                value={businessCategories}
                options={BUSINESS_CATEGORIES}
                onChange={(_, newValue) => {
                  if (newValue.length <= 3) {
                    setBusinessCategories(newValue as BusinessCategory[]);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select up to 3 business categories"
                    helperText={`${businessCategories.length}/3 selected`}
                    error={businessCategories.length === 0}
                  />
                )}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.web_url")}
              </div>
              <InputField
                value={webUrl}
                type="text"
                onChange={(value) => {
                  setWebUrl(value);
                }}
                placeholder={t("edit_company_info.content.web_url_placeholder")}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.phone_number")}
              </div>
              <InputField
                value={phoneNumber}
                type="text"
                onChange={(value) => {
                  setPhoneNumber(value);
                }}
                placeholder={t(
                  "edit_company_info.content.phone_number_placeholder"
                )}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.headquarters")}
              </div>
              <InputField
                value={headquarters}
                type="text"
                onChange={(value) => {
                  setHeadquarters(value);
                }}
                placeholder={t(
                  "edit_company_info.content.headquarters_placeholder"
                )}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.founded")}
              </div>
              <DesktopDatePicker
                value={
                  founded === 0 || founded === undefined || founded === null
                    ? null
                    : DateTime.fromFormat(founded.toString(), "yyyy")
                }
                views={["year"]}
                maxDate={DateTime.now()}
                minDate={DateTime.fromFormat("1900", "yyyy")}
                onChange={(value) => {
                  setFounded(value.year);
                }}
                // renderInput={(parameters) => <TextField {...parameters} />}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.facebook")}
              </div>
              <InputField
                value={facebook}
                type="text"
                onChange={(value) => {
                  setFacebook(value);
                }}
                placeholder={t(
                  "edit_company_info.content.facebook_placeholder"
                )}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.twitter")}
              </div>
              <InputField
                value={twitter}
                type="text"
                onChange={(value) => {
                  setTwitter(value);
                }}
                placeholder={t("edit_company_info.content.twitter_placeholder")}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.linkedin")}
              </div>
              <InputField
                value={linkedin}
                type="text"
                onChange={(value) => {
                  setLinkedin(value);
                }}
                placeholder={t(
                  "edit_company_info.content.linkedin_placeholder"
                )}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.youtube")}
              </div>
              <InputField
                value={youtube}
                type="text"
                onChange={(value) => {
                  setYoutube(value);
                }}
                placeholder={t("edit_company_info.content.youtube_placeholder")}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.instagram")}
              </div>
              <InputField
                value={instagram}
                type="text"
                onChange={(value) => {
                  setInstagram(value);
                }}
                placeholder={t(
                  "edit_company_info.content.instagram_placeholder"
                )}
              />
            </div>
            <div style={{ width: "50%", marginBottom: 24 }}>
              <div css={contentTitle}>
                {t("edit_company_info.content.tiktok")}
              </div>
              <InputField
                value={tiktok}
                type="text"
                onChange={(value) => {
                  setTiktok(value);
                }}
                placeholder={t("edit_company_info.content.tiktok_placeholder")}
              />
            </div>
            <div css={cautionStyle}>
              <AiOutlineWarning size="28px" style={{ marginRight: 8 }} />
              <div>
                <div className="whitespace-pre-wrap">
                  {/* eslint-disable-next-line react/jsx-curly-brace-presence */}
                  {t("edit_press_release.publisher.msg")}
                </div>
                <a
                  href={`https://www.vritimes.com/articles/${publisherResponse?.id}`}
                  className="font-bold underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("edit_press_release.publisher.check")}
                </a>
              </div>
            </div>
            <div className="flex justify-end my-4">
              <Button
                text={t("edit_company_info.button.save")}
                onClick={() => {
                  updateInfo();
                }}
                disabled={businessCategories.length === 0}
                className="mr-4"
              />
            </div>
          </>
        ) : (
          <div>
            <div className="mb-2 font-semibold">{t("error.login_again")}</div>
          </div>
        )}
      </PageWrapper>
    </LocalizationProvider>
  );
};

const thumbnailStyle = css`
  flex: 1;
  max-width: 50%;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 8px;
  border-color: #eeeeee;
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  cursor: pointer;
`;

const contentTitle = css`
  display: flex;
  align-items: center;
  font-style: normal;
  font-weight: 400;
  font-size: 18px;
  color: rgba(45, 45, 45, 0.8);
  margin-bottom: 8px;
  white-space: nowrap;
`;

const cautionStyle = css`
  display: flex;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 24px;
  padding: 12px;
  border-width: 2px;
  border-radius: 8px;
  border-color: #eeeeee;
  background-color: #fafafa;
  color: #808080;
`;

const BUSINESS_CATEGORIES: BusinessCategory[] = [
  "Hardware / Electronics / Peripherals",
  "Software",
  "Internet Service Provider",
  "Cloud service",
  "Internet service",
  "Blockchain / Web3",
  "Mobile app",
  "Mobile games",
  "Mobile device",
  "Camera",
  "AV equipment",
  "Cooking / Home appliances",
  "Health and Beauty appliances",
  "Servers / Peripherals",
  "Network / Network Equipment",
  "Application",
  "Security / Cybersecurity",
  "System / Website / Application development",
  "Electronic components / Semiconductors / Electrical equipment",
  "Telecommunications / VoIP",
  "Department stores / Supermarkets / Convenience stores / Stores",
  "E-commerce",
  "Wholeseller",
  "Show Business",
  "Film / Drama",
  "Music",
  "TV",
  "Comedy",
  "Magazines / Books / Publications",
  "Manga / Anime",
  "Arts and Culture",
  "NFT",
  "Gaming",
  "Toys / Playsets / Dolls",
  "Sports",
  "Outdoors / Mountain Climbing",
  "Automotive / Automotive Supplies",
  "Motorcycles / Motorcycle Supplies",
  "Bicycle / Bicycle Supplies",
  "Automotive / Motorcycle / Bicycle",
  "Car / Car Supplies",
  "Interior / Furniture / Storage",
  "Daily necessities / Household goods",
  "Garden / DIY",
  "Interior / Goods",
  "Apparel",
  "Jewelry / Accessories",
  "Kids / Baby / Maternity",
  "Food / Sweets",
  "Soft Drinks / Alcoholic drinks",
  "Restaurant / Fast food / Streetfood",
  "Eating at home / Delivery",
  "Skin care / Cosmetics / Hair products",
  "Diet / Health food / Supplement",
  "Fitness / Healthcare",
  "Medical / Hospital",
  "Pharmaceuticals",
  "Welfare / Nursing / Rehabilitation",
  "Pets / Pet Supplies",
  "Gifts / Flowers",
  "Love / Marriage",
  "Childbirth / Childcare",
  "Funeral",
  "Female Related",
  "Hotels / inns",
  "Travel / Sightseeing",
  "Theme Park / Amusement park",
  "Residential / Condominium",
  "Commercial facilities / Office buildings",
  "Architecture / Space design",
  "Construction / Civil engineering",
  "Real Estate / Architecture / Construction",
  "Full time job / Part-time job / Career",
  "Qualifications / Study Abroad / Language",
  "School / University",
  "Cram school / Preparatory school / Online education",
  "Childcare / Early childhood education",
  "HR / Recruitment",
  "Venture Capitals",
  "Banks / Credit unions",
  "Credit card / Loan",
  "Securities / FX / Investment Trusts",
  "Life insurance / Non-life insurance",
  "Cryptocurrency",
  "Advertising / Promotion / Public Relations",
  "Marketing / Research",
  "Sales",
  "Management Consulting",
  "Think tank",
  "Accounting",
  "Legal / Patent / Intellectual Property",
  "Coaching / Mentoring / Consultation",
  "Moving",
  "Logistics / Warehouse / Cargo",
  "Airlines / Aviation",
  "Steel / Metal / Glass / Earth / Stone / Rubber",
  "Chemistry",
  "Electricity / Gas / Resources / Energy",
  "STEM (Science, Tech, Engineering, Math)",
  "Textile",
  "Tabacco",
  "Nature / Weather",
  "Environment / SDGs / Recycling",
  "Green technology",
  "Government / Local government",
  "Foundations / Associations",
  "Volunteer",
  "International activity",
  "Goods and services",
  "Event",
  "Campaign",
  "Survey report",
  "Financial results announcement",
  "Public company",
  "Local companies",
  "Foreign companies",
  "Startups",
  "Non Profit Organization / NGO",
  "Robotics",
  "Artificial Intelligence / AI",
  "Agriculture",
  "Fisheries",
  "Forestry",
  "Branding / Design",
  "SMEs",
];

export default EditPublisherInfoPage;
